import { useCallback, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import type { TFunction } from "react-i18next"
import { gql } from "@apollo/client"
import { useMutation, InjectedOptions } from "@oysterhr/core"
import { DateTime } from "luxon"
import { DatePicker, Modal, Button, useToast, Toast } from "@oysterhr/ui"
import { Employment, Engagement } from "@oysterhr/schema"
import type { DocumentNode } from "graphql"
import { useForm, Controller, Control, FieldValues } from "react-hook-form"
import useUpdateEmployment from "../useUpdateEmploymentTerminationDetails"

export const APPROVE_TERMINATION_MUTATION = gql`
  mutation ApproveTermination($terminationId: ID!) {
    approveTermination(input: { terminationId: $terminationId }) {
      errors
    }
  }
`

const useApproveMutation = (options?: InjectedOptions<DocumentNode>) =>
  useMutation(APPROVE_TERMINATION_MUTATION, options)

interface ConfirmApprovalModalContentProps {
  employment: Employment
  t: TFunction
}

const ConfirmApprovalModalContent = ({ employment, t }: ConfirmApprovalModalContentProps) => {
  return (
    <div className="text-center text-s text-gray-400">
      <Trans
        defaults={t("termination.approval.subtitle", {
          name: employment?.name,
          date: DateTime.fromISO(employment?.lastDayOn).toLocaleString({
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
        })}
        components={{
          b: <b />,
        }}
      />
      <p className="mt-4">{t("termination.approval.approvalConfirmation")}</p>
    </div>
  )
}

interface LastDayOnFieldNotSetProps {
  t: TFunction
  control: Control<FieldValues>
}

const LastDayOnFieldNotSet = ({ t, control }: LastDayOnFieldNotSetProps) => {
  const [calendarOpen, setCalendarOpen] = useState(false)
  return (
    <div>
      <div className="text-center text-s text-gray-400">
        <Trans
          defaults={t("termination.approval.requiresLastDayOn")}
          components={{
            b: <b />,
          }}
        />
      </div>
      <div style={calendarOpen ? { marginBottom: "300px" } : {}}>
        <Controller
          name="lastDayOn"
          control={control}
          rules={{
            required: t<string>(
              "termination.offboardingFlow.requestDetails.errors.offboardingDate.required",
            ),
          }}
          render={({ field: { value, onChange, name, onBlur } }) => {
            return (
              <DatePicker
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={t("termination.approval.pickLastDayOn")}
                label={t("termination.approval.lastDayOn")}
                autoComplete="off"
                dateFormat="yyyy-MM-dd"
                clearable={false}
                showYearDropdown
                onCalendarOpen={() => setCalendarOpen(true)}
                onCalendarClose={() => setCalendarOpen(false)}
                scrollableYearDropdown
              />
            )
          }}
        />
      </div>
    </div>
  )
}

const isLastDayOnFieldSet = (employment: Employment) => !!employment?.lastDayOn

interface ApproveTerminationModalProps {
  employment: Employment
  engagement: Engagement
  setShowModal: (showModal: boolean) => void
  refetch: () => void
}

export function ApproveTerminationModal({
  employment,
  engagement,
  setShowModal,
  refetch,
}: ApproveTerminationModalProps) {
  // TODO: rethink the translation file organization/placement
  const { t } = useTranslation("colleague_interface/EmployeeProfilePage")
  const toast = useToast()

  const [approveMutation] = useApproveMutation()
  const [updateEmployment] = useUpdateEmployment()
  const { handleSubmit, control } = useForm()
  const activeTermination = engagement.termination
  const callApproveMutation = useCallback(async () => {
    await approveMutation({
      variables: { terminationId: activeTermination?.id },
      handlePayloadErrors: true,
    } as InjectedOptions)

    toast({
      render: (
        <Toast variation="success">
          <Toast.Content>{t("termination.approval.success")}</Toast.Content>
        </Toast>
      ),
    })

    setShowModal(false)
    refetch()
  }, [activeTermination?.id, approveMutation, refetch, toast, setShowModal, t])

  const handleLastDayOnSubmit = async () => {
    await handleSubmit(async data => {
      await updateEmployment({
        variables: {
          input: {
            companyId: employment.company.id,
            employmentId: employment.id,
            lastDayOn: data.lastDayOn,
          },
        },
        refetchQueries: ["Engagement"],
        awaitRefetchQueries: true,
      })
    })()
  }

  return (
    <Modal isOpen onRequestClose={() => setShowModal(false)}>
      <Modal.Header>{t("termination.approval.title")}</Modal.Header>
      <Modal.Content>
        <div className="px-14 pb-10" data-testid="approve-termination-modal">
          {isLastDayOnFieldSet(employment) ? (
            <ConfirmApprovalModalContent employment={employment} t={t} />
          ) : (
            <LastDayOnFieldNotSet t={t} control={control} />
          )}
        </div>
        <div className="mx-17 mb-10 mt-10 flex justify-center">
          <span
            role="presentation"
            className="w-40 cursor-pointer p-3 text-s text-primary-base underline"
            onClick={() => setShowModal(false)}
          >
            {t("termination.noGoBack")}
          </span>
          {isLastDayOnFieldSet(employment) ? (
            <Button variation="primary" onClick={callApproveMutation}>
              {t("termination.approval.submit")}
            </Button>
          ) : (
            <Button variation="primary" onClick={handleLastDayOnSubmit}>
              {t("termination.approval.approve")}
            </Button>
          )}
        </div>
      </Modal.Content>
    </Modal>
  )
}
